import { MedplumClient } from "@medplum/core";
import { Task } from "@medplum/fhirtypes";

import { ChecklistCodes } from "@/fhir/enums/quilted-health";
import { Systems } from "@/fhir/enums/systems";
import { fetchActivePregnancyCondition } from "@/pregnancy";
import { getResourcesFromBundle, joinReferenceString } from "@/utils/medplum";

export const completeChecklistTasks = async (medplum: MedplumClient, tasks: Task[]) => {
  const completedTasks = tasks.map((task) => ({ ...task, status: "completed" }));

  const updatedTasks = await Promise.all(
    completedTasks.map((task) => medplum.updateResource(task as Task)),
  );

  return updatedTasks;
};

export const getAssociatedChecklistTasks = async (
  medplum: MedplumClient,
  clientId?: string,
  sourceId?: string,
  isPatient?: boolean,
): Promise<Task[]> => {
  if (!clientId || !sourceId) return [];

  const results = await medplum.searchResources(
    "Task",
    {
      subject: joinReferenceString("Patient", clientId),
      owner: joinReferenceString(isPatient ? "Patient" : "Practitioner", sourceId),
      code: `${Systems.QH}|${ChecklistCodes.ChecklistTask}`,
    },
    {
      cache: "no-cache",
    },
  );

  const tasks = getResourcesFromBundle("Task", results.bundle);

  return tasks;
};

export const getCloseoutTask = async (
  medplum: MedplumClient,
  clientId: string,
  pregnancyConditionId?: string,
) => {
  if (!pregnancyConditionId) return;

  const task = await medplum.searchOne(
    "Task",
    {
      subject: joinReferenceString("Patient", clientId),
      focus: joinReferenceString("Condition", pregnancyConditionId),
    },
    { cache: "no-cache" },
  );

  return task;
};

export const createCloseoutTask = async ({
  medplum,
  clientId,
}: {
  medplum: MedplumClient;
  clientId?: string;
}) => {
  if (!clientId) return;

  const pregnancyCondition = await fetchActivePregnancyCondition(medplum, clientId);

  if (!pregnancyCondition?.id) return;

  const closeoutTask = await medplum.createResource({
    resourceType: "Task",
    status: "completed",
    intent: "order",
    for: { reference: joinReferenceString("Patient", clientId) },
    focus: { reference: joinReferenceString("Condition", pregnancyCondition.id) },
  });

  return closeoutTask;
};
